/**
 * Centralized Mock Data Module
 *
 * This module provides mock data for development and testing only.
 * All mock data is gated behind the shouldUseMockData() check.
 *
 * In production builds:
 * - Mock data arrays will be empty
 * - Components should handle empty states gracefully
 */

import { shouldUseMockData } from "../utils/runtime";
import type { Transaction, Asset, Wallet } from "../adapters/database";
import type { SmartAssetData } from "../components/specific/SmartAssetsComponents/SmartAssetCard";

// Re-export the check for consumers
export { shouldUseMockData };

// ============================================================================
// Order Book Mock Data (for DLEX/Exchange)
// ============================================================================

export interface MockOrderBookEntry {
  id: string;
  price: number;
  amount: number;
  total: number;
}

export interface MockOrderBook {
  bids: MockOrderBookEntry[];
  asks: MockOrderBookEntry[];
}

const DEV_ORDER_BOOK: MockOrderBook = {
  bids: [
    { id: "1", price: 1820.5, amount: 0.5, total: 910.25 },
    { id: "2", price: 1820.25, amount: 1.2, total: 2184.3 },
    { id: "3", price: 1820.0, amount: 0.8, total: 1456.0 },
    { id: "4", price: 1819.75, amount: 2.5, total: 4549.38 },
    { id: "5", price: 1819.5, amount: 1.0, total: 1819.5 },
  ],
  asks: [
    { id: "6", price: 1821.0, amount: 0.3, total: 546.3 },
    { id: "7", price: 1821.25, amount: 1.5, total: 2731.88 },
    { id: "8", price: 1821.5, amount: 0.7, total: 1275.05 },
    { id: "9", price: 1821.75, amount: 1.8, total: 3279.15 },
    { id: "10", price: 1822.0, amount: 0.4, total: 728.8 },
  ],
};

const EMPTY_ORDER_BOOK: MockOrderBook = { bids: [], asks: [] };

export function getMockOrderBook(): MockOrderBook {
  return shouldUseMockData() ? DEV_ORDER_BOOK : EMPTY_ORDER_BOOK;
}

// ============================================================================
// Chain Status Mock Data (for ConsensusStatus)
// ============================================================================

export interface ChainStatus {
  id: string;
  name: string;
  consensus: string;
  status: "synced" | "syncing" | "error";
  blockHeight: number;
}

const DEV_CHAINS: ChainStatus[] = [
  { id: "eth", name: "Ethereum", consensus: "PoS", status: "synced", blockHeight: 19250354 },
  { id: "sol", name: "Solana", consensus: "PoH", status: "synced", blockHeight: 245678901 },
  { id: "btc", name: "Bitcoin", consensus: "PoW", status: "synced", blockHeight: 876543 },
];

export function getMockChains(): ChainStatus[] {
  return shouldUseMockData() ? [...DEV_CHAINS] : [];
}

// ============================================================================
// P2P Contacts Mock Data
// Uses canonical Wikipedia "Alice and Bob" characters
// @see https://en.wikipedia.org/wiki/Alice_and_Bob
// @see packages/core/src/data/demoContacts.ts
// ============================================================================

export interface P2PContact {
  id: string;
  name: string;
  address: string;
  status: "online" | "offline";
}

/**
 * Sidebar contacts - core participants from Wikipedia "Alice and Bob" article
 * Shows only the 3 primary participants in sidebar; full list in Advanced view
 * @see packages/core/src/data/demoContacts.ts for complete 14-character list
 */
const DEV_CONTACTS: P2PContact[] = [
  { id: "alice", name: "Alice", address: "rgb:2WBcas9-yjzEvGuf-oJ8GydSx-demo", status: "online" },
  { id: "bob", name: "Bob", address: "rgb:3XCdbt0-zkAfwHvg-pK9HzeTs-demo", status: "offline" },
  { id: "carol", name: "Carol", address: "rgb:4YDeum1-alBgxIwh-qL0IafUt-demo", status: "online" },
];

export function getMockContacts(): P2PContact[] {
  return shouldUseMockData() ? [...DEV_CONTACTS] : [];
}

// ============================================================================
// Transaction Mock Data (for database adapter)
// ============================================================================

const DEV_TRANSACTIONS: Transaction[] = [
  {
    id: "1",
    amount: 1000,
    currency: "USDC",
    type: "buy",
    status: "completed",
    timestamp: new Date(Date.now() - 86400000), // 1 day ago
    toAddress: "0x1234...5678",
    txHash: "0xabcd...ef01",
    description: "Bought USDC",
  },
  {
    id: "2",
    amount: 0.5,
    currency: "ETH",
    type: "transfer",
    status: "completed",
    timestamp: new Date(Date.now() - 3600000), // 1 hour ago
    fromAddress: "0x1234...5678",
    toAddress: "0x9876...5432",
    txHash: "0xdef0...1234",
    description: "Transferred ETH",
  },
];

export function getMockTransactions(): Transaction[] {
  return shouldUseMockData() ? [...DEV_TRANSACTIONS] : [];
}

// ============================================================================
// Asset Mock Data (for database adapter)
// ============================================================================

// Note: Bitcoin is NOT included here - it comes from the real RGB infrastructure API (testnet)
// Only mock assets for chains that don't have real API integration yet
const DEV_ASSETS: Asset[] = [
  {
    id: "1",
    symbol: "ETH",
    name: "Ethereum",
    balance: 2.5,
    value: 5000,
    change24h: 2.5,
    chain: "ethereum",
    decimals: 18,
    logoUrl: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
  },
  {
    id: "2",
    symbol: "USDC",
    name: "USD Coin",
    balance: 10000,
    value: 10000,
    change24h: 0.1,
    chain: "ethereum",
    contractAddress: "0xA0b86a33E6441431CC80AE8a80A653b1aaF7b8e0",
    decimals: 6,
    logoUrl: "https://cryptologos.cc/logos/usd-coin-usdc-logo.png",
  },
  {
    id: "3",
    symbol: "SOL",
    name: "Solana",
    balance: 15.75,
    value: 3150,
    change24h: 4.2,
    chain: "solana",
    decimals: 9,
    logoUrl: "https://cryptologos.cc/logos/solana-sol-logo.png",
  },
  {
    id: "4",
    symbol: "GET",
    name: "Gold Eagle Token",
    balance: 100,
    value: 19250, // ~$192.50 per unit (1/10 oz gold @ $1925/oz)
    change24h: 0.8,
    chain: "rgb",
    decimals: 8,
    logoUrl: "./tokens/gold-eagle.svg",
  },
  {
    id: "5",
    symbol: "SET",
    name: "Silver Eagle Token",
    balance: 500,
    value: 11750, // ~$23.50 per unit (1 oz silver @ $23.50/oz)
    change24h: 1.2,
    chain: "rgb",
    decimals: 8,
    logoUrl: "./tokens/silver-eagle.svg",
  },
];

export function getMockAssets(): Asset[] {
  return shouldUseMockData() ? [...DEV_ASSETS] : [];
}

// ============================================================================
// Wallet Mock Data (for database adapter)
// ============================================================================

const DEV_WALLETS: Wallet[] = [
  {
    id: "1",
    address: "0x1234567890123456789012345678901234567890",
    chain: "ethereum",
    type: "ethereum",
    assets: DEV_ASSETS,
    totalValue: 15000,
  },
];

export function getMockWallets(): Wallet[] {
  if (!shouldUseMockData()) return [];
  // Return wallets with fresh asset references
  return DEV_WALLETS.map((wallet) => ({
    ...wallet,
    assets: getMockAssets(),
  }));
}

// ============================================================================
// SA Credits Spend Log Mock Data
// ============================================================================

export interface SpendLogEntry {
  id: string;
  type: "gas" | "ai" | "automation";
  description: string;
  amount: number;
  chain?: string;
  timestamp: Date;
}

const DEV_SPEND_LOG: SpendLogEntry[] = [
  {
    id: "1",
    type: "gas",
    description: "ETH transfer gas",
    amount: 2.5,
    chain: "eth",
    timestamp: new Date(Date.now() - 3600000),
  },
  {
    id: "2",
    type: "ai",
    description: "AI Market Advisor query",
    amount: 1.0,
    timestamp: new Date(Date.now() - 7200000),
  },
  {
    id: "3",
    type: "automation",
    description: "Auto payment: OpenAI API",
    amount: 15.0,
    timestamp: new Date(Date.now() - 86400000),
  },
  {
    id: "4",
    type: "gas",
    description: "SOL transfer",
    amount: 0.1,
    chain: "sol",
    timestamp: new Date(Date.now() - 172800000),
  },
  {
    id: "5",
    type: "automation",
    description: "Auto payment: Anthropic API",
    amount: 20.0,
    timestamp: new Date(Date.now() - 259200000),
  },
];

export function getMockSpendLog(): SpendLogEntry[] {
  return shouldUseMockData() ? [...DEV_SPEND_LOG] : [];
}

// ============================================================================
// PAIR Numeraire Price Data
// ============================================================================

/**
 * PAIR/USD conversion rate
 * PAIR is the base numeraire for SA Credits and normalized value flows
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/MockSmartAssetTemplates/PAIR.md
 */
export const PAIR_USD_PRICE = 3.05; // $3.05 per PAIR

export function getPairUsdPrice(): number {
  return PAIR_USD_PRICE;
}

/**
 * Convert USD value to PAIR
 */
export function usdToPair(usdValue: number): number {
  return usdValue / PAIR_USD_PRICE;
}

/**
 * Convert PAIR value to USD
 */
export function pairToUsd(pairValue: number): number {
  return pairValue * PAIR_USD_PRICE;
}

// ============================================================================
// Credits Balance Mock Data
// ============================================================================

export interface CreditsBalance {
  balance: number; // in PAIR
  burnRate: number; // PAIR per day
  autoRefillEnabled: boolean;
}

const DEV_CREDITS_BALANCE: CreditsBalance = {
  balance: 847.5, // 847.5 PAIR (~$2,584.88 USD)
  burnRate: 12.5, // 12.5 PAIR per day (~$38.13 USD)
  autoRefillEnabled: false,
};

const EMPTY_CREDITS_BALANCE: CreditsBalance = {
  balance: 0,
  burnRate: 0,
  autoRefillEnabled: false,
};

// Runtime state for auto-refill (persisted in memory during session)
let _autoRefillEnabled = false;

export function getMockCreditsBalance(): CreditsBalance {
  if (!shouldUseMockData()) {
    return { ...EMPTY_CREDITS_BALANCE };
  }
  return {
    ...DEV_CREDITS_BALANCE,
    autoRefillEnabled: _autoRefillEnabled,
  };
}

/**
 * Set the auto-refill enabled state.
 * This persists in memory during the session.
 */
export function setAutoRefillEnabled(enabled: boolean): void {
  _autoRefillEnabled = enabled;
}

/**
 * Get the current auto-refill enabled state.
 */
export function getAutoRefillEnabled(): boolean {
  return shouldUseMockData() ? _autoRefillEnabled : false;
}

// ============================================================================
// Smart Asset Units Mock Data (for partitioning)
// ============================================================================

const DEFAULT_SMART_ASSET_UNITS = 100;
const DEV_SMART_ASSET_UNITS: Record<string, number> = {
  "smart-treasury": 250,
  "bounty-forge": 80,
  get: 100,
  set: 0, // User doesn't hold units - shows Notifications tab instead of Governance
  pair: 60,
  "tax-manager": 90,
  barq: 0,
  "cc-htlc": 30,
  perpetuals: 0,
  "p2p-perpetuals": 0,
  "htlc-service": 0,
};

export function getMockSmartAssetUnits(assetId?: string): number {
  if (!shouldUseMockData()) return 0;
  if (!assetId) return DEFAULT_SMART_ASSET_UNITS;
  return DEV_SMART_ASSET_UNITS[assetId] ?? DEFAULT_SMART_ASSET_UNITS;
}

// ============================================================================
// Governance Mock Data (for Smart Asset governance tab)
// ============================================================================

export interface GovernanceProposal {
  id: string;
  title: string;
  description: string;
  proposer: string;
  status: "active" | "passed" | "rejected";
  createdAt: string;
  votingEndsAt: string;
  options: string[];
  quorumRequired: number;
  votesCast: number;
  results: Record<string, number>;
  userVote: string | null;
  type: "standard" | "whitelist" | "recovery";
  recoveryInfo?: {
    targetTreasury: string;
    unresponsiveSigners: number;
    totalSigners: number;
    proposerStake: number;
    signerResponseWindow: string;
    signersResponded: number;
  };
}

export interface VotingHistoryEntry {
  proposalId: string;
  proposalTitle: string;
  choice: string;
  votingPower: number;
  timestamp: string;
  outcome: "passed" | "rejected" | "pending";
}

const DEV_PROPOSALS: GovernanceProposal[] = [
  {
    id: "prop-001",
    title: "Increase Treasury Allocation to Development",
    description: `## Summary\n\nThis proposal seeks to increase the development fund allocation from 10% to 15% of monthly treasury inflows.\n\n## Rationale\n\nWith the upcoming roadmap expansion, additional resources are needed for:\n- Smart contract audits\n- UI/UX improvements\n- Infrastructure scaling\n\n## Implementation\n\nIf passed, the treasury smart contract will be updated to route 15% of inflows to the development multisig.`,
    proposer: "alice.satch",
    status: "active",
    createdAt: "2025-12-20T10:00:00Z",
    votingEndsAt: "2025-12-29T10:00:00Z",
    options: ["For", "Against", "Abstain"],
    quorumRequired: 1000,
    votesCast: 650,
    results: { For: 420, Against: 180, Abstain: 50 },
    userVote: null,
    type: "standard",
  },
  {
    id: "prop-002",
    title: "Add BTC Cold Storage Address to Whitelist",
    description: `## Summary\n\nProposal to add a new Bitcoin cold storage address to the TradTreasury whitelist.\n\n## Address Details\n\n- **Address**: bc1q...xyz\n- **Type**: Native SegWit (P2WPKH)\n- **Purpose**: Long-term BTC reserves\n\n## Security Verification\n\n- Hardware wallet verified\n- Multi-sig protected\n- Geographically distributed keys`,
    proposer: "bob.satch",
    status: "active",
    createdAt: "2025-12-22T14:00:00Z",
    votingEndsAt: "2025-12-31T14:00:00Z",
    options: ["For", "Against", "Abstain"],
    quorumRequired: 1500,
    votesCast: 890,
    results: { For: 640, Against: 200, Abstain: 50 },
    userVote: "For",
    type: "whitelist",
  },
  {
    id: "prop-003",
    title: "Q4 Yield Distribution Parameters",
    description: `## Summary\n\nSet yield distribution parameters for Q4 2025.\n\n## Proposed Parameters\n\n- Base yield: 4.2% APY\n- Performance bonus: Up to 1.5%\n- Distribution frequency: Daily`,
    proposer: "charlie.satch",
    status: "passed",
    createdAt: "2025-12-01T09:00:00Z",
    votingEndsAt: "2025-12-08T09:00:00Z",
    options: ["For", "Against", "Abstain"],
    quorumRequired: 1000,
    votesCast: 1200,
    results: { For: 950, Against: 150, Abstain: 100 },
    userVote: "For",
    type: "standard",
  },
  {
    id: "prop-004",
    title: "Emergency Signer Recovery",
    description: `## Recovery Request\n\n2 of 5 TradTreasury signers have been unresponsive for 35 days.\n\n## Proposed New Signers\n\n- 0x123...abc (verified identity)\n- 0x456...def (verified identity)\n\n## Evidence\n\n- Last signer activity: Nov 20, 2025\n- Multiple contact attempts documented`,
    proposer: "dave.satch",
    status: "active",
    createdAt: "2025-12-25T08:00:00Z",
    votingEndsAt: "2026-01-08T08:00:00Z",
    options: ["For", "Against"],
    quorumRequired: 1500,
    votesCast: 320,
    results: { For: 280, Against: 40 },
    userVote: null,
    type: "recovery",
    recoveryInfo: {
      targetTreasury: "SETs TradTreasury",
      unresponsiveSigners: 2,
      totalSigners: 5,
      proposerStake: 1000,
      signerResponseWindow: "2 days",
      signersResponded: 3,
    },
  },
];

const DEV_VOTING_HISTORY: VotingHistoryEntry[] = [
  {
    proposalId: "prop-003",
    proposalTitle: "Q4 Yield Distribution Parameters",
    choice: "For",
    votingPower: 150,
    timestamp: "2025-12-05T14:30:00Z",
    outcome: "passed",
  },
  {
    proposalId: "prop-002",
    proposalTitle: "Add BTC Cold Storage Address to Whitelist",
    choice: "For",
    votingPower: 150,
    timestamp: "2025-12-23T11:00:00Z",
    outcome: "pending",
  },
];

export function getMockProposals(): GovernanceProposal[] {
  return shouldUseMockData() ? [...DEV_PROPOSALS] : [];
}

export function getMockVotingHistory(): VotingHistoryEntry[] {
  return shouldUseMockData() ? [...DEV_VOTING_HISTORY] : [];
}

// ============================================================================
// Schedule Mock Data (for Smart Asset schedule tab)
// ============================================================================

export interface ScheduleGroup {
  id: string;
  label: string;
  count: number;
}

export interface ScheduleItem {
  id: string;
  name: string;
  amount: string;
  frequency: string;
  nextDate: string;
  status: "active" | "paused" | "needs-attention";
  destination: string;
}

const DEV_SCHEDULE_DAYS = [5, 10, 15, 20, 25];

const DEV_SCHEDULE_GROUPS: ScheduleGroup[] = [
  { id: "upcoming", label: "Upcoming", count: 3 },
  { id: "active", label: "Active Rules", count: 2 },
  { id: "paused", label: "Paused", count: 1 },
  { id: "needs-attention", label: "Needs Attention", count: 1 },
];

const DEV_SCHEDULE_ITEMS: ScheduleItem[] = [
  {
    id: "rule-1",
    name: "Monthly Treasury Disbursement",
    amount: "0.25 BTC",
    frequency: "Monthly",
    nextDate: "Jan 15",
    status: "active",
    destination: "Operations SATCHEL",
  },
  {
    id: "rule-2",
    name: "Yield Sweep to ETH",
    amount: "1.2 ETH",
    frequency: "Weekly",
    nextDate: "Jan 10",
    status: "active",
    destination: "Liquidity SATCHEL",
  },
  {
    id: "rule-3",
    name: "GETs Contribution",
    amount: "500 GETs",
    frequency: "Quarterly",
    nextDate: "Mar 01",
    status: "paused",
    destination: "Governance Vault",
  },
  {
    id: "rule-4",
    name: "SOL Reserve Refill",
    amount: "40 SOL",
    frequency: "Monthly",
    nextDate: "Jan 25",
    status: "needs-attention",
    destination: "Treasury Buffer",
  },
];

export function getMockScheduleDays(): number[] {
  return shouldUseMockData() ? [...DEV_SCHEDULE_DAYS] : [];
}

export function getMockScheduleGroups(): ScheduleGroup[] {
  return shouldUseMockData() ? [...DEV_SCHEDULE_GROUPS] : [];
}

export function getMockScheduleItems(): ScheduleItem[] {
  return shouldUseMockData() ? [...DEV_SCHEDULE_ITEMS] : [];
}

// ============================================================================
// Smart Asset Data (for Dashboard and Master Container)
// ============================================================================

/**
 * Mock Smart Asset definitions for development
 * This is the single source of truth for Smart Asset mock data
 */
const DEV_SMART_ASSETS: SmartAssetData[] = [
  {
    id: "smart-treasury",
    name: "SmartTreasury",
    ticker: "STR",
    description: "Autonomous portfolio manager with deep network allocation.",
    category: "primitive",
    status: "demo",
    iconPath: "./assets/smart-assets/SmartTreasury.png",
    valueFlowsDiagram: "./assets/smart-assets/value-flows/SampleSmartTreasury.png",
    governanceEnabled: true,
    governanceConfig: {
      votingMultiplier: 1.5,
      holderClass: "Contributor",
      minHoldingPeriod: 7,
      proposalThreshold: 100,
    },
  },
  {
    id: "pair",
    name: "PAIR",
    ticker: "PAIR",
    description: "Oracle & yield numeraire for normalized Smart Asset value.",
    category: "primitive",
    status: "prototype",
    iconPath: "./assets/smart-assets/PAIR.png",
    governanceEnabled: true,
    governanceConfig: {
      votingMultiplier: 1.0,
      holderClass: "Observer",
      minHoldingPeriod: 30,
      proposalThreshold: 1000,
    },
  },
  {
    id: "tax-manager",
    name: "TaxManager",
    ticker: "TAX",
    description: "Tax-efficient routing for outflows with compliance rules.",
    category: "primitive",
    status: "prototype",
    iconPath: "./assets/smart-assets/TaxManager.png",
    unitsTransferrable: false,
    governanceEnabled: false,
  },
  {
    id: "cc-htlc",
    name: "cc-HTLC",
    ticker: "CCH",
    description: "Cross-chain settlement.",
    category: "primitive",
    status: "prototype",
    governanceEnabled: false,
  },
  {
    id: "barq",
    name: "BARQ",
    ticker: "BARQ",
    description: "Lightning routing rewards.",
    category: "primitive",
    status: "prototype",
    unitsTransferrable: false,
    governanceEnabled: false,
  },
  {
    id: "bounty-forge",
    name: "BountyForge",
    ticker: "BF",
    description: "Incentives and bounties.",
    category: "primitive",
    status: "demo",
    iconPath: "./assets/smart-assets/BountyForge.png",
    unitsTransferrable: false,
    governanceEnabled: true,
    governanceConfig: {
      votingMultiplier: 1.5,
      holderClass: "Contributor",
      minHoldingPeriod: 7,
      proposalThreshold: 50,
      supervotingRules: [
        { holderClass: "Founder", multiplier: 3.0, color: "#8B5CF6" },
        { holderClass: "Seed Financier", multiplier: 2.0, color: "#F59E0B" },
        { holderClass: "Financier", multiplier: 1.0, color: "#10B981" },
        { holderClass: "Contributor", multiplier: 1.5, color: "#3B82F6" },
      ],
    },
  },
  {
    id: "get",
    name: "GET",
    ticker: "GET",
    description: "Governance and exchange network.",
    category: "primitive",
    status: "prototype",
    iconPath: "./assets/smart-assets/GET.png",
    governanceEnabled: true,
    governanceConfig: {
      votingMultiplier: 2.0,
      holderClass: "Financier",
      minHoldingPeriod: 14,
      proposalThreshold: 50,
    },
  },
  {
    id: "set",
    name: "SET",
    ticker: "SET",
    description: "Governance and exchange network.",
    category: "primitive",
    status: "prototype",
    iconPath: "./assets/smart-assets/SET.png",
    governanceEnabled: true,
    governanceConfig: {
      votingMultiplier: 2.0,
      holderClass: "Financier",
      minHoldingPeriod: 14,
      proposalThreshold: 50,
    },
  },
  {
    id: "perpetuals",
    name: "Perpetuals",
    ticker: "PERP",
    description: "Derivatives / Perpetuals.",
    category: "primitive",
    status: "prototype",
    governanceEnabled: false,
  },
  {
    id: "p2p-perpetuals",
    name: "P2P Perpetuals",
    ticker: "P2P",
    description: "Derivatives.",
    category: "primitive",
    status: "prototype",
    governanceEnabled: false,
  },
  {
    id: "htlc-service",
    name: "HTLC Service",
    ticker: "HTLC",
    description: "Infrastructure.",
    category: "primitive",
    status: "prototype",
    governanceEnabled: false,
  },
];

export function getMockSmartAssets(): SmartAssetData[] {
  return shouldUseMockData() ? [...DEV_SMART_ASSETS] : [];
}

/**
 * Get a specific Smart Asset by ID
 */
export function getMockSmartAssetById(assetId: string): SmartAssetData | undefined {
  if (!shouldUseMockData()) return undefined;
  return DEV_SMART_ASSETS.find((asset) => asset.id === assetId);
}
